/**
 * Turning a Firebase Auth failure into something a person can act on (§3).
 *
 * There are two kinds of sign-in failure and they need opposite treatment.
 *
 * A *credential* failure must stay vague. Distinguishing "no such account" from
 * "wrong password" hands an attacker an account-enumeration oracle: they can
 * confirm which addresses are registered without ever signing in. Every code in
 * VAGUE collapses to one message for that reason.
 *
 * A *configuration* failure must be exact. "Email/password sign-in is switched
 * off for this project" and "that password is wrong" are indistinguishable to
 * the person typing, but the fixes live in different buildings — one in the
 * Firebase console, one in their head. Reporting the first as the second is how
 * an afternoon disappears. None of these codes reveal anything about who holds
 * an account, so there is nothing to protect by being coy.
 *
 * Pure and exported so it can be tested without a browser or a Firebase project.
 */

/** Codes that must never be told apart: each one confirms or denies an account. */
const VAGUE = new Set([
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-email',
  'auth/user-disabled',
]);

const GENERIC = 'That email and password did not match.';

/** Configuration and environment failures, said plainly. */
const EXPLAINED: Record<string, string> = {
  'auth/operation-not-allowed':
    'Email and password sign-in is not switched on for this Firebase project. ' +
    'An administrator enables it in Firebase console → Authentication → Sign-in method.',
  'auth/network-request-failed':
    'Could not reach Firebase. Check the connection and try again.',
  'auth/too-many-requests':
    'Too many attempts from this device. Wait a few minutes and try again.',
  'auth/unauthorized-domain':
    'This web address is not on the project’s list of authorized domains. ' +
    'An administrator adds it in Firebase console → Authentication → Settings → Authorized domains.',
  'auth/invalid-api-key':
    'The Firebase API key in this build was rejected. The values are inlined at ' +
    'build time, so this is fixed in web/apphosting.yaml and deployed again.',
  'auth/api-key-not-valid':
    'The Firebase API key in this build was rejected. The values are inlined at ' +
    'build time, so this is fixed in web/apphosting.yaml and deployed again.',
  'auth/app-check-token-is-invalid':
    'App Check rejected this app. If enforcement was switched on before a build ' +
    'carrying the reCAPTCHA site key was deployed, switch it off until one is.',
  'auth/requires-recent-login':
    'This account needs to sign in again before that can be changed.',
};

/**
 * The `code` off a thrown value, or null if it does not carry one.
 *
 * A FirebaseError puts it on `.code`; anything else (a TypeError from a broken
 * build, a string thrown by a polyfill) does not, and must not be read as one.
 */
export function authErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && code.startsWith('auth/') ? code : null;
}

/**
 * What to show someone whose sign-in failed.
 *
 * An unrecognized code is reported *with the code attached* rather than folded
 * into the generic message. Being wrong about which bucket a failure belongs in
 * is worse than being unpolished: a code in the message can be searched for, and
 * a rep who reads one out has told support everything they need.
 */
export function describeAuthError(err: unknown): string {
  const code = authErrorCode(err);
  if (!code) {
    return 'Sign-in could not be completed. Please try again.';
  }
  if (VAGUE.has(code)) return GENERIC;
  const explained = EXPLAINED[code];
  if (explained) return explained;
  return `Sign-in failed (${code}).`;
}
